/**
 * Lo que el navegador del teléfono tapa o mueve, publicado para el CSS.
 *
 * `--browser-ui-bottom`: cuánto tapa por abajo la barra del navegador. Se
 * sigue midiendo, pero en el teléfono el CSS lo deja en cero: allí se desplaza
 * el documento (ver `lib/canvasScroll.ts`), y lo pegado a la pantalla ya se
 * apoya solo encima de la barra, que además se encoge al desplazar.
 *
 * `data-keyboard`, `--visible-top` y `--visible-height`: con el teclado
 * abierto, dónde está y cuánto mide la franja que se ve, para que la barra de
 * arriba y la de formato de texto se queden en ella.
 *
 * Se mide y no se supone: cambia de un iPhone a otro, de una versión de Safari
 * a otra, y al girar.
 */
let installed = false

export function installBrowserChrome() {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return
  installed = true
  const probe = document.createElement('div')
  probe.setAttribute('aria-hidden', 'true')
  probe.style.cssText = 'position:fixed;left:0;top:0;width:0;height:100lvh;visibility:hidden;pointer-events:none'
  document.body.appendChild(probe)
  const update = () => {
    // Sin `lvh` (navegadores viejos) el alto sale 0 y no se tapa nada: 0.
    const covered = Math.max(0, Math.round(probe.getBoundingClientRect().height - window.innerHeight))
    document.documentElement.style.setProperty('--browser-ui-bottom', `${covered}px`)
  }
  update()
  window.addEventListener('resize', update)
  window.visualViewport?.addEventListener('resize', update)

  const keyboardOpen = () => {
    const viewport = window.visualViewport
    return Boolean(viewport && viewport.height < window.innerHeight * 0.75)
  }

  /*
   * Con el teclado abierto, lo que va pegado a la pantalla sigue a lo que se ve.
   *
   * Al subir el teclado Safari mueve la vista para enseñar el cursor, y lo que
   * va pegado a la pantalla —la barra de arriba, la de formato— se quedaba
   * fuera de ella. Aquí se publica cuánto se ha corrido la vista (`offsetTop`)
   * y cuánto mide, y el CSS las lleva ahí. Ver `data-keyboard` en 22-touch.css.
   */
  const syncVisible = () => {
    const viewport = window.visualViewport
    if (!viewport) return
    const root = document.documentElement
    const open = root.dataset.formFactor === 'phone' && keyboardOpen()
    root.dataset.keyboard = open ? 'open' : 'closed'
    root.style.setProperty('--visible-top', `${Math.round(open ? viewport.offsetTop : 0)}px`)
    root.style.setProperty('--visible-height', `${Math.round(viewport.height)}px`)
  }
  syncVisible()
  window.visualViewport?.addEventListener('resize', syncVisible)
  window.visualViewport?.addEventListener('scroll', syncVisible)

  /*
   * En el teléfono, la barra de arriba se esconde al bajar y vuelve al subir.
   *
   * Es lo que hace la propia barra del navegador: bajando se está leyendo, y la
   * barra sólo quita sitio; subiendo, lo normal es que se busque algo de ella.
   * Siempre visible cerca del principio, y con el teclado abierto —se está
   * escribiendo y hace falta el contexto—. Los movimientos de menos de 6px no
   * cuentan: el temblor del dedo la hacía parpadear. Ver `data-topbar` en
   * 22-touch.css.
   */
  let lastY = window.scrollY
  window.addEventListener('scroll', () => {
    const root = document.documentElement
    if (root.dataset.formFactor !== 'phone') {
      if (root.dataset.topbar) delete root.dataset.topbar
      return
    }
    const y = window.scrollY
    const delta = y - lastY
    if (Math.abs(delta) < 6) return
    lastY = y
    const show = y < 64 || delta < 0 || root.dataset.keyboard === 'open'
    root.dataset.topbar = show ? 'shown' : 'hidden'
  }, { passive: true })
}
